import { Link } from '@mui/material';

import {
  Divider,
  EllipsisWithTooltip,
  LinkBehaviour,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { OptimizationWorkload } from '@cast/types';

import { Link as RouterLink } from 'components/router';
import { configuredByLabels } from 'features/cluster/workload-optimization/_constants';
import { isManagedByAnnotations } from 'features/workload-optimization';

import type { TypographyProps } from '@cast/design-system';

type WorkloadSummaryData = Pick<
  OptimizationWorkload,
  | 'namespace'
  | 'kind'
  | 'version'
  | 'group'
  | 'isCustom'
  | 'managedBy'
  | 'isJobLike'
>;

type Props = {
  workload: WorkloadSummaryData;
  namespaceLink?: string;
  labelColor?: TypographyProps['color'];
};

export const WorkloadDetailsDrawerSummary = ({
  workload,
  namespaceLink,
  labelColor = 'grey.500',
}: Props) => {
  return (
    <Box
      border="1px solid"
      borderColor={tokens.theme.border.structural.neutral}
      borderRadius="4px"
      p={16}
      data-testid="workload-summary"
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        divider={<Divider orientation="vertical" flexItem />}
      >
        <Stack rowGap={4} width={88} data-testid="namespace-item">
          <Typography variant="L10R" color={labelColor}>
            NAMESPACE:
          </Typography>
          {namespaceLink ? (
            <Link to={namespaceLink} component={RouterLink} underline="none">
              <LinkBehaviour secondaryAction variant="P12M" noWrap>
                {workload.namespace}
              </LinkBehaviour>
            </Link>
          ) : (
            <EllipsisWithTooltip TypographyProps={{ variant: 'P12R' }}>
              {workload.namespace}
            </EllipsisWithTooltip>
          )}
        </Stack>

        <Stack rowGap={4} width={88} data-testid="type-item">
          <Typography variant="L10R" color={labelColor}>
            TYPE:
          </Typography>
          <EllipsisWithTooltip TypographyProps={{ variant: 'P12R' }}>
            {workload.isJobLike ? `${workload.kind} (job-like)` : workload.kind}
          </EllipsisWithTooltip>
        </Stack>

        <Stack rowGap={4} width={88} data-testid="configured-by-item">
          <Typography variant="L10R" color={labelColor}>
            CONFIGURED BY:
          </Typography>
          <EllipsisWithTooltip TypographyProps={{ variant: 'P12R' }}>
            {isManagedByAnnotations(workload)
              ? configuredByLabels.ANNOTATIONS
              : configuredByLabels.API}
          </EllipsisWithTooltip>
        </Stack>

        <Stack rowGap={4} width={88} data-testid="api-item">
          <Typography variant="L10R" color={labelColor}>
            API:
          </Typography>
          <EllipsisWithTooltip TypographyProps={{ variant: 'P12R' }}>
            {`${workload.version}/${workload.group}`}
          </EllipsisWithTooltip>
        </Stack>

        <Stack rowGap={4} width={88} data-testid="custom-item">
          <Typography variant="L10R" color={labelColor}>
            CUSTOM:
          </Typography>
          <EllipsisWithTooltip TypographyProps={{ variant: 'P12R' }}>
            {workload.isCustom ? 'YES' : 'NO'}
          </EllipsisWithTooltip>
        </Stack>
      </Stack>
    </Box>
  );
};
